/** The interact half's slice of the scene contract, derived from the sdk document, never restated.
 *
 * The render half derives its own slice in `render/scene/contract.ts` and deliberately leaves
 * `input`, `action` and `gate` out of it. This is the other side of that line: the two fields a
 * control layer reads and the broadcast build has no code for at all.
 */

import type { SceneNode } from "../render/scene/contract";

export type {
  DataBinding,
  FlowLayout,
  LayoutBox,
  Repeat,
  SceneDocument,
  SceneNode,
  SceneNodeKind,
} from "../render/scene/contract";

export { BAR_VALUE_KEY, SUPPORTED_SCHEMA_VERSION } from "../render/scene/contract";

/** What a press on a node does: the already-declared submission it feeds, and the value it sends. */
export type SceneAction = NonNullable<SceneNode["action"]>;

/** Whether a node's action is armed or locked for whoever this document was assembled for. */
export type GateState = NonNullable<SceneNode["gate"]>;

/** The physical key a control claims, matched against `event.code` and never against `event.key`. */
export type KeyCode = NonNullable<SceneNode["key_code"]>;

/** What a canvas does with a control its holder cannot yet use. */
export type GateMode = NonNullable<GateState["mode"]>;

/** The kinds a control layer draws. Flat and open, the same shape as the render half's list.
 *
 * `input` is the one kind only this half has: the render half's allowlist does not carry it, so a
 * control cannot reach the broadcast bundle by somebody forgetting to strip it.
 */
export const CONTROL_KINDS = ["group", "text", "bar", "input"] as const;

export type ControlKind = (typeof CONTROL_KINDS)[number];

const CONTROLS: ReadonlySet<string> = new Set<string>(CONTROL_KINDS);

/** Whether this half draws the kind. An unlisted kind is not an error, it is not drawn. */
export function isControlKind(kind: string): kind is ControlKind {
  return CONTROLS.has(kind);
}

/** The kind that carries an action, and the only one this half makes pressable. */
export const INPUT_KIND = "input";

/** The gate mode that omits a locked node instead of drawing it locked. */
export const GATE_MODE_HIDE = "hide";

/** The gate mode that draws a locked node in place, carrying the host's own label. */
export const GATE_MODE_SHOW_LOCKED = "show_locked";
